import { useEffect, useMemo, useState } from "react";
import api from "../utils/api"; // 🔥 değişti

// Cache configuration
const EPG_CACHE_KEY_PREFIX = "epg_cache_";
const EPG_CACHE_DURATION = 60 * 60 * 1000; // 1 hour

function getCachedEpg(channelId, keyDate) {
  try {
    const cacheKey = `${EPG_CACHE_KEY_PREFIX}${channelId}_${keyDate}`;

    const cached = sessionStorage.getItem(cacheKey);

    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    if (now - timestamp < EPG_CACHE_DURATION) {
      return data;
    }

    sessionStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    console.error("Error reading EPG cache:", error);
    return null;
  }
}

function setCachedEpg(channelId, keyDate, data) {
  try {
    const cacheKey = `${EPG_CACHE_KEY_PREFIX}${channelId}_${keyDate}`;
    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        data,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    console.error("Error setting EPG cache:", error);
  }
}

function toDateSafe(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function minuteOfDay(d) {
  if (!d) return null;
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

function deriveWeekdayInt(epgItem) {
  if (epgItem?.weekday_int !== undefined && epgItem?.weekday_int !== null) {
    const n = Number(epgItem.weekday_int);
    return Number.isFinite(n) ? n : null;
  }

  if (epgItem?.key_date) {
    const d = new Date(`${epgItem.key_date}T00:00:00`);
    if (!Number.isNaN(d.getTime())) {
      const js = d.getDay();
      return js === 0 ? 7 : js;
    }
  }

  const map = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
  };

  return map[String(epgItem?.weekday || "").toLowerCase()] ?? null;
}

export function useEpgData(channelId, keyDate, options = {}) {
  const { startDateRange, endDateRange } = options;
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!channelId) {
        setRaw([]);
        setError("");
        setLoading(false);
        return;
      }

      const cacheId = startDateRange && endDateRange
        ? `range_${startDateRange}_${endDateRange}`
        : keyDate;
      const cached = getCachedEpg(channelId, cacheId);

      if (cached) {
        setRaw(cached);
        setError("");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const url = startDateRange && endDateRange
          ? `/api/EpgLogs?channel_id=${encodeURIComponent(channelId)}&start_date_range=${encodeURIComponent(startDateRange)}&end_date_range=${encodeURIComponent(endDateRange)}`
          : `/api/EpgLogs?channel_id=${encodeURIComponent(channelId)}&key_date=${encodeURIComponent(keyDate)}`;

        const res = await api.get(url);

        if (!cancelled) {
          const epgData = Array.isArray(res.data) ? res.data : [];
          setRaw(epgData);
          setCachedEpg(channelId, cacheId, epgData);
        }
      } catch (e) {
        if (cancelled) return;

        const status = e?.response?.status;

        const msg =
          e?.response?.data?.error ||
          e?.message ||
          "Failed to fetch EPG data";

        setError(status ? `${status}: ${msg}` : msg);
        setRaw([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [channelId, keyDate, startDateRange, endDateRange]);


  const epgBlocks = useMemo(() => {
    const blocks = raw
      .map((x) => {
        const startDate = toDateSafe(x.start_date || x.StartDate);
        const endDate = toDateSafe(x.end_date || x.EndDate);

        const startMinute = minuteOfDay(startDate);
        const endMinute = minuteOfDay(endDate);

        const keyDate =
          x.key_date ||
          x.Keydate ||
          (startDate ? startDate.toISOString().slice(0, 10) : "");

        return {
          id: x.id || x._id || "",
          programName: x.program_name || x.ProgramName || "",
          startDate,
          endDate,
          startMinute,
          endMinute,
          keyDate,
          weekdayInt: deriveWeekdayInt(x),
          _raw: x,
        };
      })
      .filter(
        (b) =>
          b.id &&
          b.programName &&
          b.startDate &&
          b.endDate &&
          b.startMinute !== null &&
          b.endMinute !== null
      );

    blocks.sort((a, b) => a.startMinute - b.startMinute);

    return blocks;
  }, [raw]);

  return { epgBlocks, loading, error };
}